import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  CropFree as SelectionIcon,
  Gesture as FreeSelectionIcon,
  Category as PolygonIcon,
  FilterList as GeoFiltersIcon,
  Streetview as StreetViewIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  LocationCity as CityIcon,
  Public as CountryIcon,
  Place as StateIcon,
  Timeline as ZipCodeIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

export type ToolbarMode = 'selection' | 'freeSelection' | 'polygon' | 'streetView';

export interface GeoFilterType {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const GEO_FILTER_TYPES: GeoFilterType[] = [
  { id: 'cities', label: 'Cities', icon: <CityIcon fontSize="small" /> },
  { id: 'states', label: 'States/Provinces', icon: <StateIcon fontSize="small" /> },
  { id: 'countries', label: 'Countries', icon: <CountryIcon fontSize="small" /> },
  { id: 'zipCodes', label: 'ZIP/Postal Codes', icon: <ZipCodeIcon fontSize="small" /> },
  { id: 'businesses', label: 'Business Districts', icon: <BusinessIcon fontSize="small" /> }
];

interface MapToolbarProps {
  activeMode: ToolbarMode;
  isFullscreen: boolean;
  onModeChange: (mode: ToolbarMode) => void;
  onGeoFilterSelect: (filterType: string) => void;
  onFullscreenToggle: () => void;
  disabled?: boolean;
  trayOpen?: boolean;
}

/**
 * Map toolbar component
 * Provides map interaction tools in a rounded toolbar at the bottom-center
 */
export const MapToolbar: React.FC<MapToolbarProps> = ({
  activeMode,
  isFullscreen,
  onModeChange,
  onGeoFilterSelect,
  onFullscreenToggle,
  disabled = false,
  trayOpen = false
}) => {
  const [geoFiltersAnchor, setGeoFiltersAnchor] = useState<HTMLElement | null>(null);

  const handleGeoFiltersClick = (event: React.MouseEvent<HTMLElement>) => {
    setGeoFiltersAnchor(event.currentTarget);
  };

  const handleGeoFiltersClose = () => {
    setGeoFiltersAnchor(null);
  };

  const handleGeoFilterSelect = (filterType: string) => {
    onGeoFilterSelect(filterType);
    handleGeoFiltersClose();
  };

  const getButtonStyle = (isActive: boolean = false) => ({
    background: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    border: isActive ? '2px solid rgba(25, 118, 210, 0.5)' : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '8px',
    width: 40,
    height: 40,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: isActive ? 'rgba(25, 118, 210, 0.16)' : 'rgba(240, 240, 240, 0.95)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)'
    }
  });

  return (
    <>
      <Box
        className={`map-toolbar${trayOpen ? ' tray-open' : ''}`}
        sx={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 15,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(12px)',
          borderRadius: '24px',
          padding: '8px 16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        {/* Selection Tool */}
        <Tooltip title="Selection Tool" placement="top">
          <IconButton
            onClick={() => onModeChange('selection')}
            disabled={disabled}
            size="small"
            sx={getButtonStyle(activeMode === 'selection')}
          >
            <SelectionIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Free Selection Mode */}
        <Tooltip title="Free Selection" placement="top">
          <IconButton
            onClick={() => onModeChange('freeSelection')}
            disabled={disabled}
            size="small"
            sx={getButtonStyle(activeMode === 'freeSelection')}
          >
            <FreeSelectionIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Polygon Tool */}
        <Tooltip title="Polygon Tool" placement="top">
          <IconButton
            onClick={() => onModeChange('polygon')}
            disabled={disabled}
            size="small"
            sx={getButtonStyle(activeMode === 'polygon')}
          >
            <PolygonIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Divider */}
        <Box
          sx={{
            width: '1px',
            height: '24px',
            background: 'rgba(0, 0, 0, 0.12)',
            mx: 0.5
          }}
        />

        {/* Geo-Filters */}
        <Tooltip title="Geographic Filters" placement="top">
          <IconButton
            onClick={handleGeoFiltersClick}
            disabled={disabled}
            size="small"
            sx={getButtonStyle(Boolean(geoFiltersAnchor))}
          >
            <GeoFiltersIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Street View */}
        <Tooltip title="Street View" placement="top">
          <IconButton
            onClick={() => onModeChange('streetView')}
            disabled={disabled}
            size="small"
            sx={getButtonStyle(activeMode === 'streetView')}
          >
            <StreetViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Divider */}
        <Box
          sx={{
            width: '1px',
            height: '24px',
            background: 'rgba(0, 0, 0, 0.12)',
            mx: 0.5
          }}
        />

        {/* Fullscreen Toggle */}
        <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} placement="top">
          <IconButton
            onClick={onFullscreenToggle}
            disabled={disabled}
            size="small"
            sx={getButtonStyle(isFullscreen)}
          >
            {isFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Geo-Filters Menu */}
      <Menu
        anchorEl={geoFiltersAnchor}
        open={Boolean(geoFiltersAnchor)}
        onClose={handleGeoFiltersClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            borderRadius: '12px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            mt: -1
          }
        }}
      >
        {GEO_FILTER_TYPES.map((filter) => (
          <MenuItem
            key={filter.id}
            onClick={() => handleGeoFilterSelect(filter.id)}
            sx={{
              borderRadius: '8px',
              mx: 1,
              my: 0.5,
              '&:hover': {
                background: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {filter.icon}
            </ListItemIcon>
            <ListItemText primary={filter.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
